import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { ActionIcon, Group, Modal } from "@mantine/core"
import { useCallback, useEffect, useRef, useState } from "react"

export interface ILightboxEntry {
  url: string
  alt: string
}

interface IUseImageLightboxResult {
  openLightbox: (images: ILightboxEntry[], startIndex?: number) => void
  LightboxModal: React.JSX.Element
}

/**
 * Full-screen navigable image lightbox.
 * Supports keyboard (← → ESC), arrow buttons, and touch swipe.
 */
export function useImageLightbox(): IUseImageLightboxResult {
  const { i18n } = useLingui()
  const [images, setImages] = useState<ILightboxEntry[]>([])
  const [index, setIndex] = useState(0)
  const opened = images.length > 0

  const openLightbox = useCallback((imgs: ILightboxEntry[], startIndex = 0): void => {
    setImages(imgs)
    setIndex(startIndex)
  }, [])

  const close = useCallback((): void => setImages([]), [])

  const prev = useCallback((): void => setIndex((i) => (i > 0 ? i - 1 : i)), [])
  const next = useCallback((): void => setIndex((i) => (i < images.length - 1 ? i + 1 : i)), [images.length])

  // Keyboard navigation
  useEffect(() => {
    if (!opened) return
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "ArrowLeft") prev()
      else if (e.key === "ArrowRight") next()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [opened, prev, next])

  // Touch swipe
  const touchStartX = useRef<number | null>(null)

  const handleTouchStart = useCallback((e: React.TouchEvent): void => {
    touchStartX.current = e.touches[0]?.clientX ?? null
  }, [])

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent): void => {
      if (touchStartX.current === null) return
      const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current
      if (Math.abs(dx) > 40) {
        if (dx < 0) next()
        else prev()
      }
      touchStartX.current = null
    },
    [next, prev],
  )

  const current = images[index]
  const hasPrev = index > 0
  const hasNext = index < images.length - 1

  const LightboxModal = (
    <Modal
      opened={opened}
      onClose={close}
      size="100%"
      padding={0}
      withCloseButton={false}
      styles={{
        content: { background: "transparent", boxShadow: "none", overflow: "visible" },
        overlay: { backdropFilter: "blur(6px)", background: "rgba(0,0,0,0.85)" },
        inner: { padding: 0 },
      }}
      centered
    >
      {current && (
        <div
          style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: keyboard nav (← → ESC) handled by window listener above */}
          <img
            src={current.url}
            alt={current.alt}
            onClick={close}
            style={{
              maxWidth: "95vw",
              maxHeight: "95vh",
              objectFit: "contain",
              borderRadius: "var(--mantine-radius-md)",
              display: "block",
              cursor: "zoom-out",
            }}
          />

          {/* Prev / Next */}
          {hasPrev && (
            <ActionIcon
              variant="filled"
              color="dark"
              size="xl"
              radius="xl"
              onClick={(e) => {
                e.stopPropagation()
                prev()
              }}
              style={{
                position: "fixed",
                left: 16,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
                opacity: 0.85,
              }}
              aria-label={i18n._(msg`Previous image`)}
            >
              ‹
            </ActionIcon>
          )}
          {hasNext && (
            <ActionIcon
              variant="filled"
              color="dark"
              size="xl"
              radius="xl"
              onClick={(e) => {
                e.stopPropagation()
                next()
              }}
              style={{
                position: "fixed",
                right: 16,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
                opacity: 0.85,
              }}
              aria-label={i18n._(msg`Next image`)}
            >
              ›
            </ActionIcon>
          )}

          {/* Counter + close */}
          <Group style={{ position: "fixed", top: 12, right: 12, zIndex: 10 }} gap="xs">
            {images.length > 1 && (
              <span style={{ color: "rgba(255,255,255,0.7)", fontSize: 13 }}>
                {index + 1} / {images.length}
              </span>
            )}
            <ActionIcon
              variant="filled"
              color="dark"
              size="sm"
              radius="xl"
              onClick={close}
              aria-label={i18n._(msg`Close`)}
            >
              ✕
            </ActionIcon>
          </Group>
        </div>
      )}
    </Modal>
  )

  return { openLightbox, LightboxModal }
}
